import React, { useState, useEffect } from "react";
import "antd/dist/antd.css";
import { Badge, Card, Row, Col } from "antd";
import { useDispatch, useSelector } from "react-redux";
import moment from "moment";
import StandardTableLayout from "../../layouts/StandardTableLayout/StandardTableLayout";
import StandardListLayout from "../../layouts/StandardTableLayout/StandardListLayout";
import {
  FETCH_ALL_VEHICLES,
  setVehicles,
  DELETE_VEHICLE,
} from "../../store/fleet-store/reducer";

const FeetVehicle = () => {
  const dispatch = useDispatch();
  const { organisationId } = useSelector(({ auth }) => auth.user);
  const { vehicles } = useSelector(({ fleets }) => fleets.fleetReducer);
  const [isLoading, setIsLoading] = useState(true);
  const { csvExportData } = useSelector(({ user }) => user.userReducer);
  const [dataList, setDataList] = useState([]);
  const [filteredVehicles, setFilteredVehicles] = useState([]);
  const [serviceFilter, setServiceFilter] = useState(null); // null, 'upcoming', 'overdue'

  // Calculate service status for vehicles
  const calculateServiceStatus = (vehicle) => {
    const lastOdo = parseInt(vehicle.lastOdometer) || 0;
    const nextServiceOdo = parseInt(vehicle.nextServiceOdometer) || 0;
    
    if (nextServiceOdo === 0) return 'no-service'; // No service scheduled
    
    const difference = nextServiceOdo - lastOdo;
    
    if (difference <= 0) return 'overdue';
    if (difference <= 500) return 'upcoming';
    return 'good';
  };

  // Get vehicles by service status
  const getVehiclesByServiceStatus = (status) => {
    return vehicles?.filter(vehicle => calculateServiceStatus(vehicle) === status) || [];
  };

  // Get upcoming service vehicles (within 500km)
  const upcomingServiceVehicles = getVehiclesByServiceStatus('upcoming');
  
  // Get overdue service vehicles
  const overdueServiceVehicles = getVehiclesByServiceStatus('overdue');

  // Handle service filter click
  const handleServiceFilterClick = (filterType) => {
    if (serviceFilter === filterType) {
      // If clicking the same filter, clear it
      setServiceFilter(null);
      setFilteredVehicles(vehicles || []);
    } else {
      // Apply new filter
      setServiceFilter(filterType);
      if (filterType === 'upcoming') {
        setFilteredVehicles(upcomingServiceVehicles);
      } else if (filterType === 'overdue') {
        setFilteredVehicles(overdueServiceVehicles);
      }
    }
  };

  const columns = [
    {
      title: "ID",
      dataIndex: "autoNumber",
      key: "autoNumber",
      sorter: (a, b) => a.autoNumber - b.autoNumber,
      width: 80,
    },
    {
      title: "Nick Name",
      dataIndex: "nickName",
      key: "nickName",
      sorter: (a, b) => a.nickName.localeCompare(b.nickName),
    },
    {
      title: "Fuel Type",
      dataIndex: "fuelType",
      key: "fuelType",
      sorter: (a, b) => a.fuelType.localeCompare(b.fuelType),
    },
    {
      title: "Registration",
      dataIndex: "registration",
      key: "registration",
      sorter: (a, b) => a.registration.localeCompare(b.registration),
    },
    {
      title: "Vin Number",
      dataIndex: "vinNumber",
      key: "vinNumber",
      sorter: (a, b) => a.vinNumber.localeCompare(b.vinNumber),
    },
    {
      title: "Engin Number",
      dataIndex: "enginNumber",
      key: "enginNumber",
      sorter: (a, b) => a.enginNumber.localeCompare(b.enginNumber),
    },
    {
      title: "Last Odometer Date",
      dataIndex: "lastOdometerDate",
      key: "lastOdometerDate",
      render: (date) => {
        if (!date) return "-";
        return moment(date).format("DD-MMM-YYYY");
      },
      sorter: (a, b) => {
        if (!a.lastOdometerDate && !b.lastOdometerDate) return 0;
        if (!a.lastOdometerDate) return 1;
        if (!b.lastOdometerDate) return -1;
        return moment(a.lastOdometerDate).unix() - moment(b.lastOdometerDate).unix();
      },
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status) => {
        let type = status === true ? "success" : "error";
        return <Badge status={type} text={status ? "Active" : "InActive"} />;
      },
      sorter: (a, b) => b.status - a.status,
    },
  ];

  const [csv, setCsv] = useState({
    headers: [
      {
        label: "Nick Name",
        key: "nickName",
      },
      {
        label: "Vin Number",
        key: "vinNumber",
      },
      {
        label: "Engin Number",
        key: "enginNumber",
      },
      {
        label: "Category",
        key: "category",
      },
      {
        label: "Make Model",
        key: "make",
      },
      {
        label: "GVM",
        key: "GVM",
      },
      {
        label: "TARE",
        key: "TARE",
      },
      {
        label: "Last Odo Meter",
        key: "lastOdometer",
      },
      {
        label: "Last Odo Meter Date",
        key: "lastOdometerDate",
      },
      {
        label: "Registration",
        key: "registration",
      },
      {
        label: "Seats",
        key: "seats",
      },
      {
        label: "Fuel Type",
        key: "fuelType",
      },
      {
        label: "Year",
        key: "year",
      },
      {
        label: "Toll Id",
        key: "tollId",
      },
      {
        label: "Notes",
        key: "notes",
      },
      {
        label: "Driver Url",
        key: "driverUrl",
      },
      {
        label: "Color",
        key: "color",
      },
      {
        label: "Status",
        key: "status",
      },
    ],
    data: [],
    fileName: "allFleetVehicles.csv",
  });

  const handleColumnCsv = (array) => {
    let csvData = [];
    array.forEach((e) => {
      csvData.push({
        nickName: e.nickName,
        vinNumber: e.vinNumber,
        enginNumber: e.enginNumber,
        category: e.category?.description,
        make: e.make?.description,
        TARE: e.TARE,
        GVM: e.GVM,
        lastOdometer: e.lastOdometer,
        lastOdometerDate: e.lastOdometerDate,
        registration: e.registration,
        seats: e.seats,
        fuelType: e.fuelType,
        year: e.year,
        tollId: e.tollId,
        notes: e.notes,
        status: e.status,
        driverUrl: e.driverUrl,
        color: e.color,
      });
    });
    return csvData;
  };

  const FETCH_DATA = () => {
    if (organisationId) {
      FETCH_ALL_VEHICLES(organisationId)
        .then((data) => dispatch(setVehicles(data)))
        .finally(() => setIsLoading(false));
    }
  };
  useEffect(() => {
    FETCH_DATA();
  }, [organisationId]);

  useEffect(() => {
    if (vehicles) {
      const columns = handleColumnCsv(vehicles);
      setCsv({
        ...csv,
        data: columns,
      });
      // Initialize filtered vehicles with all vehicles
      setFilteredVehicles(vehicles);
    }
  }, [vehicles]);

  useEffect(() => {
    if (csvExportData.length > 0) {
      const columns = handleColumnCsv(csvExportData);
      setCsv({
        ...csv,
        data: columns,
      });
    }
  }, [csvExportData]);

  useEffect(() => {
    const newDoc = [];
    filteredVehicles?.forEach((ele, index) => {
      let data = {
        _id: ele?._id,
        title: `${ele?.autoNumber} - ${ele?.nickName}`,
        description: `${ele?.fuelType} - ${ele?.vinNumber} - ${ele?.enginNumber}`,
      };
      newDoc.push(data);
    });
    setDataList(newDoc);
  }, [filteredVehicles]);

  const ExtraSection = () => {
    return (
      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col span={12}>
          <Card 
            hoverable 
            onClick={() => handleServiceFilterClick('upcoming')}
            style={{ 
              cursor: 'pointer',
              border: serviceFilter === 'upcoming' ? '1px solid #fa8c16' : '1px solid transparent',
              backgroundColor: serviceFilter === 'upcoming' ? '#fff7e6' : '#fff',
              textAlign: 'center',
              minHeight: 'auto'
            }}
            bodyStyle={{ padding: '16px' }}
          >
            <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#fa8c16', marginBottom: '4px' }}>
              {upcomingServiceVehicles.length}
            </div>
            <div style={{ fontSize: '14px', color: '#666' }}>
              Upcoming Service
            </div>
          </Card>
        </Col>
        <Col span={12}>
          <Card 
            hoverable 
            onClick={() => handleServiceFilterClick('overdue')}
            style={{ 
              cursor: 'pointer',
              border: serviceFilter === 'overdue' ? '1px solid #ff4d4f' : '1px solid transparent',
              backgroundColor: serviceFilter === 'overdue' ? '#fff2f0' : '#fff',
              textAlign: 'center',
              minHeight: 'auto'
            }}
            bodyStyle={{ padding: '16px' }}
          >
            <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#ff4d4f', marginBottom: '4px' }}>
              {overdueServiceVehicles.length}
            </div>
            <div style={{ fontSize: '14px', color: '#666' }}>
              Overdue Service
            </div>
          </Card>
        </Col>
      </Row>
    );
  };

  return (
    <div className=" table-organisations table-payments">
      <div className="table-layouts">
        <div className="tables">
          <StandardTableLayout
            csvHeader={csv.headers || null}
            csvData={csv.data || null}
            csvFileName={csv.fileName || null}
            setIsLoading={setIsLoading}
            isLoading={isLoading}
            hierachy={[{ key: 1, label: "Fleet Vehicle" }]}
            dataSource={filteredVehicles}
            columns={columns}
            FETCH_DATA={FETCH_DATA}
            DELETE_FUNC={DELETE_VEHICLE}
            table="fleet/vehicle"
            isPagination={false}
            ExtraSection={ExtraSection}
          />
        </div>
        <div className="table-lists">
          <StandardListLayout
            csvHeader={csv.headers || null}
            csvData={csv.data || null}
            csvFileName={csv.fileName || null}
            setIsLoading={setIsLoading}
            isLoading={isLoading}
            hierachy={[{ key: 1, label: "Fleet Vehicle" }]}
            dataList={dataList}
            columns={columns}
            FETCH_DATA={FETCH_DATA}
            DELETE_FUNC={DELETE_VEHICLE}
            table="fleet/vehicle"
          />
        </div>
      </div>
    </div>
  );
};

export default FeetVehicle;
